import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSave,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

// ✅ safe localStorage getter
const getLS = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const WorkTypeEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  const loginBranchId = Number(branchId || 0);
  const branchid = Number(branchId || 0);
  const loginuserid = Number(userId || 0);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [branches, setBranches] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const [showFilters, setShowFilters] = useState(true);
  const [formData, setFormData] = useState({
    workType: "",
    specification: "",
  });

  const [errors, setErrors] = useState({});
  const [workTypeRecords, setWorkTypeRecords] = useState([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [currentWorktypeId, setCurrentWorktypeId] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  const fetchBranches = async (worktypeid = 0) => {
    try {
      setLoadingBranches(true);

      const res = await axios.get(`${BASE_URL}/api/workType/branches`, {
        params: { branchid, worktypeid },
        headers: commonHeaders,
      });

      const list = (res.data.list || []).map((b, idx) => ({
        ...b,
        sNo: idx + 1,
        selected: Boolean(b.isMapped),
      }));

      setBranches(list);

      const selected = list.filter((x) => x.selected).map((x) => x.branchid);
      setSelectedBranches(selected);
      setSelectAll(list.length > 0 && selected.length === list.length);
    } catch (err) {
      console.error(err);
      toast.error("Server error while loading branches");
    } finally {
      setLoadingBranches(false);
    }
  };

  const fetchWorkTypes = async (p = page, searchOverride) => {
    try {
      setLoadingWorkTypes(true);

      const searchValue = searchOverride !== undefined ? searchOverride : search;

      const res = await axios.get(`${BASE_URL}/api/workType/list`, {
        params: {
          branchid,
          page: p,
          pageSize,
          search: searchValue,
        },
        headers: commonHeaders,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Failed to load work types");
        return;
      }

      const list = (res.data.list || []).map((r) => ({
        id: r.worktypeid,
        sNo: r.rowno,
        workType: r.worktypename,
        specification: r.specification,
        selected: false,
      }));

      setWorkTypeRecords(list);
      setTotal(res.data.total || 0);
      setPage(res.data.page || p);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Server error while loading work types"
      );
    } finally {
      setLoadingWorkTypes(false);
    }
  };

  useEffect(() => {
    fetchBranches(0);
    if (branchid) fetchWorkTypes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectWorkTypeRow = (row) => {
    const wid = row.id;

    setCurrentWorktypeId(wid);
    setFormData({
      workType: row.workType || "",
      specification: row.specification || "",
    });

    fetchBranches(wid);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.workType.trim()) {
      newErrors.workType = "Work Type is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectAllBranches = (checked) => {
    setSelectAll(checked);

    setBranches((prev) => {
      const updated = prev.map((b) => ({ ...b, selected: checked }));
      setSelectedBranches(checked ? updated.map((b) => b.branchid) : []);
      return updated;
    });
  };

  const handleSelectBranch = (bid, checked) => {
    setBranches((prev) => {
      const updated = prev.map((b) =>
        b.branchid === bid ? { ...b, selected: checked } : b
      );

      const selected = updated.filter((x) => x.selected).map((x) => x.branchid);
      setSelectedBranches(selected);
      setSelectAll(updated.length > 0 && selected.length === updated.length);

      return updated;
    });
  };

  const handleAddNew = () => {
    setFormData({
      workType: "",
      specification: "",
    });

    setCurrentWorktypeId(0);
    fetchBranches(0);

    toast.info("Ready to add new Work Type");
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fill in required fields");
      return;
    }

    if (selectedBranches.length === 0) {
      toast.error("Please select at least one branch");
      return;
    }

    try {
      const isEdit = currentWorktypeId > 0;

      const url = isEdit
        ? `${BASE_URL}/api/workType/update`
        : `${BASE_URL}/api/workType/save`;

      const payload = {
        ...(isEdit ? { worktypeid: currentWorktypeId } : {}),
        worktype: formData.workType,
        specification: formData.specification,
        branchid,
        loginuserid,
        branchIds: selectedBranches,
      };

      const res = await axios.post(url, payload, {
        headers: commonHeaders,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Save failed");
        return;
      }

      toast.success(
        res.data.message ||
          (isEdit ? "Successfully Updated" : "Successfully Inserted")
      );

      const returnedId = Number(res.data.worktypeid || 0);
      const finalId = isEdit ? currentWorktypeId : returnedId;

      if (finalId) {
        setCurrentWorktypeId(finalId);
        fetchBranches(finalId);
      } else {
        setCurrentWorktypeId(0);
        fetchBranches(0);
      }

      fetchWorkTypes(1);

      setFormData({ workType: "", specification: "" });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Server error while saving");
    }
  };

  const handleRemoveWorkType = async () => {
    const selectedIds = workTypeRecords
      .filter((r) => r.selected)
      .map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.error("Please select work types to remove");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to remove ${selectedIds.length} work type(s)?`
    );
    if (!ok) return;

    try {
      const res = await axios.delete(`${BASE_URL}/api/workType/delete`, {
        params: {
          branchid,
          ids: selectedIds.join(","),
        },
        headers: commonHeaders,
      });

      if (!res.data.success) {
        if (res.data.blockedIds?.length) {
          toast.warn(
            "Some work types are referred in projects and cannot be removed"
          );
        } else {
          toast.error(res.data.message || "Delete failed");
        }
        return;
      }

      toast.success(res.data.message || "Deleted Successfully");

      fetchWorkTypes(1);

      if (selectedIds.includes(currentWorktypeId)) {
        setCurrentWorktypeId(0);
        setFormData({ workType: "", specification: "" });
        fetchBranches(0);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Server error while deleting"
      );
    }
  };

    const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleSearchSubmit = () => {
    fetchWorkTypes(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };
  const handleClearSearch = () => {
    setSearch("");
    fetchWorkTypes(1, "");
  };

  const handleSelectWorkType = (id, checked) => {
    setWorkTypeRecords((prev) =>
      prev.map((record) =>
        record.id === id ? { ...record, selected: checked } : record
      )
    );
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK TYPE ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white text-black`
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Work Type Entry
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search work type, specification..."
                  className={`px-3 py-2 rounded-lg border text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <button
                  onClick={handleSearchSubmit}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white text-sm`}
                >
                  Search
                </button>
                {search && (
                  <button
                    onClick={handleClearSearch}
                    className={`px-4 py-2 rounded-lg font-semibold border transition-all duration-200 text-sm ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showFilters && (
              <div
                className={`transition-all duration-300 ease-in-out ${
                  showFilters
                    ? "max-h-none opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className={`p-6 border-t ${
                    isDark
                      ? "bg-gray-800 border-gray-700"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="max-w-4xl mx-auto space-y-4">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-3">
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Work Type <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={formData.workType}
                          onChange={(e) =>
                            handleInputChange("workType", e.target.value)
                          }
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors.workType ? "border-red-500" : ""
                          } ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter work type..."
                        />
                        {errors.workType && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.workType}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-3">
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Specification
                        </label>
                      </div>
                      <div className="col-span-9">
                        <textarea
                          value={formData.specification}
                          onChange={(e) =>
                            handleInputChange("specification", e.target.value)
                          }
                          rows={3}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter specification..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl border mb-6 overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className={`p-4 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Branches</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={selectAll}
                    onChange={(e) =>
                      handleSelectAllBranches(e.target.checked)
                    }
                    className={`rounded focus:ring-2 focus:ring-blue-500 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-blue-600"
                        : "bg-white border-gray-300 text-blue-600"
                    }`}
                  />
                  <label
                    htmlFor="selectAll"
                    className={`text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Select All
                  </label>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Branches
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Select
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loadingBranches ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center">
                          Loading branches...
                        </td>
                      </tr>
                    ) : branches.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-6 text-center text-sm opacity-70"
                        >
                          No branches found
                        </td>
                      </tr>
                    ) : (
                      branches.map((branch, index) => (
                        <tr
                          key={branch.branchid}
                          className={`transition-colors duration-200 ${
                            index % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {branch.sNo}
                          </td>
                          <td
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {branch.branchname || "-"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={branch.selected}
                              onChange={(e) =>
                                handleSelectBranch(
                                  branch.branchid,
                                  e.target.checked
                                )
                              }
                              className={`rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-blue-600"
                                  : "bg-white border-gray-300 text-blue-600"
                              }`}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPlus />
              Add New
            </button>

            <button
              onClick={handleSave}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaSave />
              Save
            </button>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Slno
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Work Type
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Specification
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Select
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingWorkTypes ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center">
                        Loading work types...
                      </td>
                    </tr>
                  ) : workTypeRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center text-sm opacity-70"
                      >
                        No work types found
                      </td>
                    </tr>
                  ) : (
                    workTypeRecords.map((record, index) => (
                      <tr
                        key={record.id}
                        onClick={() => handleSelectWorkTypeRow(record)}
                        className={`cursor-pointer transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        } ${
                          currentWorktypeId === record.id
                            ? "ring-2 ring-blue-500"
                            : ""
                        }`}
                      >
                        <td
                          className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.sNo}
                        </td>
                        <td
                          className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          {record.workType}
                        </td>
                        <td
                          className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.specification || "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={record.selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleSelectWorkType(record.id, e.target.checked)
                            }
                            className={`rounded focus:ring-2 focus:ring-blue-500 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-blue-600"
                                : "bg-white border-gray-300 text-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleRemoveWorkType}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-red-700 hover:bg-red-600 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              <FaTrash />
              Remove
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default WorkTypeEntry;